import { Option } from "@nozzlegear/railway";
import { Paths, getPathRegex } from "shared/paths";
import { hasOnlySampleItems } from "shared/has-only-sample-items";

/**
 * The subset of a cart line item that determines whether checkout must skip the
 * deceased-name step. Kept structural so callers and tests don't need full line items.
 */
export interface CartGuardItem {
    type?: string;
}

export interface CartCheckoutState {
    /** Mirrors `Dev.allow_cart`. Corporate sites disable the cart overview and use the stepped flow. */
    allowCart: boolean;
    items: ReadonlyArray<CartGuardItem>;
    /** True once the user (or this guard) has entered the checkout flow. */
    startedCheckout: boolean;
    /** True once the shipping step has stored an address; city and state are required before continuing. */
    shippingCompleted: boolean;
}

/**
 * Computes where the cart guard must redirect for `location`, or none when the user may stay.
 * Pure: callers own navigation and any `startCheckout` side effect.
 */
export function tryMustRedirect(location: string, state: CartCheckoutState): Option<string> {
    if (!state.allowCart) {
        // /cart path is disabled
        // User must start checkout from /cart/name
        // User must have at least one line item
        // CartStore must have Cart.startedCheckout eval to true
        if (state.items.length === 0) {
            return Option.ofSome(Paths.quick.index);
        }

        // Sample-only carts skip the name step entirely. /cart/name renders nothing for them,
        // so send them to shipping instead. They still have to complete shipping before details.
        if (hasOnlySampleItems(state.items)) {
            if (getPathRegex(Paths.cart.shipping).test(location)) {
                return Option.ofNone();
            }

            if (state.shippingCompleted && getPathRegex(Paths.cart.details).test(location)) {
                return Option.ofNone();
            }

            return Option.ofSome(Paths.cart.shipping);
        }

        if (getPathRegex(Paths.cart.index).test(location)) {
            return Option.ofSome(Paths.cart.name);
        } else if (!state.startedCheckout && !getPathRegex(Paths.cart.name).test(location)) {
            return Option.ofSome(Paths.cart.name);
        }

        // Every order type must go through shipping before details, even on a direct link.
        if (!state.shippingCompleted && getPathRegex(Paths.cart.details).test(location)) {
            return Option.ofSome(Paths.cart.shipping);
        }

        return Option.ofNone();
    }

    // If the cart is empty, the user has either not started checkout or has already submitted
    // an order (Cart.reset() was called). Redirect to /quick so back-navigation after a
    // successful order submission cannot land on a cart page.
    if (state.items.length === 0) {
        return Option.ofSome(Paths.quick.index);
    }

    // User must start checkout from /cart
    // CartStore.startedCheckout gets set when they press the startCheckout button.
    if (!state.startedCheckout && !getPathRegex(Paths.cart.index).test(location)) {
        return Option.ofSome(Paths.cart.index);
    }

    return Option.ofNone();
}
